<template>
  <UIModal v-model="isOpen" :index-override="3000" @closed="resetForm">
    <template v-if="token" #header>
      <div class="tw:flex tw:items-center tw:gap-2">
        <img :alt="token.key" :src="tokens[token.key as unknown as Tokens]?.toString()" class="tw:w-10 tw:h-10" />

        <h6 class="tw:font-medium tw:text-white tw:text-2xl">{{ computedTokenName }}</h6>
      </div>
    </template>
    <template v-if="token" #default>
      <div class="tw:sm:min-w-md">
        <div class="tw:flex tw:flex-col tw:gap-2 tw:p-2">
          <div
            class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:shadow-xl tw:p-2 tw:border tw:border-[#1E353E] tw:rounded-xl"
          >
            <div class="tw:flex tw:flex-col tw:items-center">
              <span>{{ $t('walletOwnedTokens') }}</span>
              <span class="tw:text-white">{{ formatAmount(token.balance) }}</span>
            </div>
            <template v-if="isSharable">
              <div class="tw:flex tw:flex-col tw:items-center">
                <span>{{ $t('walletRewardOwned') }}</span>
                <span class="tw:text-white">{{ formatAmount(token.earnedBalance) }}</span>
              </div>
              <div class="tw:flex tw:justify-between tw:items-center tw:w-full">
                <div class="tw:flex tw:flex-col tw:items-center">
                  <span>{{ $t('walletMyShare') }}</span>
                  <span class="tw:text-white">{{ Number(token.shared).toFixed(2) }}%</span>
                </div>
                <NuxtLink class="tw:ml-auto" target="_blank" to="/dashboard">
                  <HalloweenGenericButton>{{ $t('walletStatistics') }}</HalloweenGenericButton>
                </NuxtLink>
              </div>
            </template>
          </div>
          <div
            class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:shadow-xl tw:p-2 tw:border tw:border-[#1E353E] tw:rounded-xl"
          >
            <UIInput
              v-model="receiverAddress"
              :placeholder="$t('walletAddress')"
              class="tw:bg-[#5C5D5E]/50 tw:px-2 tw:rounded-md tw:w-full tw:h-8"
            />
            <UINumberInput v-model="amount" :max="new BigNumber(token.balance)" :min="0.0001" />
            <HalloweenGenericButton
              :disabled="!receiverAddress || !amount || isProcessing"
              @click="sendToken(receiverAddress, amount, token?.tokenContractAddress)"
              >{{ $t('walletSend') }}</HalloweenGenericButton
            >
          </div>
          <div
            class="tw:flex tw:justify-between tw:items-center tw:gap-2 tw:shadow-xl tw:p-2 tw:border tw:border-[#1E353E] tw:rounded-xl"
          >
            <div class="tw:flex tw:items-center tw:gap-2">
              <template v-if="isSupported">
                <IconsCopy
                  v-if="!copied"
                  class="tw:w-5 tw:h-5"
                  @click="handleCopyAddress(token?.tokenContractAddress)"
                />
                <IconsCheckMark v-else class="tw:w-5 tw:h-5 tw:text-white" />
              </template>
              <NuxtLink :to="getTokenAddressLink(token?.tokenContractAddress)" external target="_blank">
                <IconsLinkNewTab class="tw:w-5 tw:h-5" />
              </NuxtLink>
            </div>
            <AddTokenButton :token="token" />
          </div>
        </div>
      </div>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { BigNumber } from 'bignumber.js';
import { AddTokenButton } from '~/features/wallet';
import { Erc20Token__factory } from '~/public/abi/evm/types';
import tokens from '~/public/img/tokens';
import type { StorageItem } from '~/types/apiService';
import { Tokens, tokensConfig } from '~/utils/constants';

const { token } = defineProps<{
  token: StorageItem | null;
}>();

const isOpen = defineModel<boolean>({ default: false });

const storageItemsEventBus = useEventBus<void>('storage-items');

const {
  blockchain: { blockScanUrl }
} = useEnvs();
const { sendContractMethod } = useSendContractMethod();
const { getSigner } = useProvider();
const store = useMainStore();
const { t } = useI18n();

const secondaryTokens = [Tokens.blackPearl, Tokens.crystallineOre, Tokens.shiningMetalite, Tokens.acorn];
const isSharable = computed(
  () => !token?.isNft && (token?.isSellable || secondaryTokens.includes(token?.key as Tokens))
);

const receiverAddress = ref<string>('');
const amount = ref<string>('0');
const isProcessing = ref(false);

function resetForm() {
  receiverAddress.value = '';
  amount.value = '0';
  isProcessing.value = false;
}

async function sendToken(to: string, amount: string, tokenAddress: string) {
  const amountRaw = new BigNumber(amount)
    .decimalPlaces(18, BigNumber.ROUND_DOWN)
    .shiftedBy(18)
    .integerValue(BigNumber.ROUND_FLOOR)
    .toFixed(0);

  try {
    isProcessing.value = true;
    const transaction = await sendContractMethod({
      contract: Erc20Token__factory.connect(tokenAddress, await getSigner()),
      methodName: 'transfer(address,uint256)',
      args: [to, amountRaw]
    });

    if (!transaction) return false;

    saveHashToLocalStorage(t('walletTransferToken'), transaction.hash);
    store.updateVersion();

    await transaction.wait(2);

    storageItemsEventBus.emit();

    modifyHistoryHashStatus(transaction.hash, 'Done');
    store.updateVersion();
  } catch (error) {
    console.log('error', error);
  } finally {
    isProcessing.value = false;
  }
}

const computedTokenName = computed(() => t(tokensConfig[token?.key as Tokens]?.label || '', { count: 0 }));

const getTokenAddressLink = (tokenAddress: string) => blockScanUrl + '/token/' + tokenAddress;

const { copy, isSupported, copied } = useClipboard();
const handleCopyAddress = (tokenAddress: string) => copy(tokenAddress);

function formatAmount(amount: string) {
  const number = new BigNumber(amount);

  if (number.isNaN() || number.lt(0) || number.isZero()) {
    return '0';
  }

  if (number.lt(0.01)) {
    return '<0.01';
  }

  if (number.gte(10000)) {
    return number.toFormat(0);
  }

  return number.toFormat(2);
}
</script>
